/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBoxOpen, FaSearch } from "react-icons/fa";
import { HiOutlineArrowRight } from "react-icons/hi";
import BookingRemaining from "../SharedComponents/BookingRemaining";
import PieChartComponenet from "../SharedComponents/PieChartComponenet";
import PaymentHistoryTable from "../SharedComponents/PaymentHistoryTable";
import axios from "axios";
import BarChartComponent from "../SharedComponents/BarChartComponent";
import Box from "../../../setupPage/SharedComponents/Box";
import { formatCurrency } from "../../../../utils/formatCurrency";

const isPaidStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (!value) return false;
  if (value.includes("unpaid") || value.includes("pending")) return false;
  return value.includes("paid") || value.includes("complete");
};

const Subroot = () => {
  const apiAddress = process.env.REACT_APP_API_URL;
  let userId = localStorage.getItem("sundorbonBookingUserDetails");
  userId = JSON.parse(userId);

  const [fetchedData, setFetchedData] = useState(null);
  const [boxData, setBoxData] = useState(null);
  const [loading, setLoading] = useState(false);

  const displayName =
    userId?.Name || userId?.Email?.split("@")[0] || "there";

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  useEffect(() => {
    if (!userId?.Id) return;
    setLoading(true);
    axios
      .get(`${apiAddress}ParcelBookingInformation/agent/${userId.Id}`)
      .then((res) => {
        setFetchedData(res.data);
      })
      .catch((err) => {
        console.log(err.message);
      })
      .finally(() => setLoading(false));

    axios
      .get(
        `${apiAddress}AgentBoxAssign/AgentBoxAssignDetailsByAgentId/${userId.Id}`,
      )
      .then((res) => {
        setBoxData(res.data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, []);

  const bookings = Array.isArray(fetchedData) ? fetchedData : [];
  const boxes = Array.isArray(boxData) ? boxData : [];

  const stats = useMemo(() => {
    const paid = bookings.filter((row) => isPaidStatus(row.paymentStatus)).length;
    const commission = bookings.reduce(
      (sum, row) => sum + (parseFloat(row.agentCommisionPayable) || 0),
      0,
    );
    const volume = bookings.reduce(
      (sum, row) => sum + (parseFloat(row.subTotal) || 0),
      0,
    );
    const currency =
      bookings.find((row) => row.symbol || row.currencyType)?.symbol ||
      bookings.find((row) => row.currencyType)?.currencyType ||
      "GBP";
    const limit = boxes.reduce(
      (sum, row) => sum + (Number(row.limitCurrentMonth) || 0),
      0,
    );
    const used = boxes.reduce(
      (sum, row) => sum + (Number(row.boxAssignCurrentMonth) || 0),
      0,
    );
    return {
      total: bookings.length,
      paid,
      unpaid: Math.max(0, bookings.length - paid),
      commission,
      volume,
      currency,
      limit,
      used,
      remaining: Math.max(0, limit - used),
    };
  }, [bookings, boxes]);

  const pieData = useMemo(() => {
    if (stats.limit > 0) {
      return [
        { name: "Used", value: stats.used, color: "#ea580c" },
        { name: "Remaining", value: stats.remaining, color: "#fed049" },
      ].filter((item) => item.value > 0);
    }
    return [
      { name: "Paid", value: stats.paid || 0, color: "#15803d" },
      { name: "Open", value: stats.unpaid || 0, color: "#FFA76A" },
    ].filter((item) => item.value > 0);
  }, [stats]);

  const barDataset = useMemo(() => {
    if (boxes.length) {
      return boxes.map((item) => ({
        month: String(item.boxName || "Box").replaceAll("_", " "),
        value: Number(item.boxAssignCurrentMonth) || 0,
      }));
    }
    const grouped = {};
    bookings.forEach((row) => {
      const key = row.dimensionName || row.cargoTypeName || "Other";
      grouped[key] = (grouped[key] || 0) + (parseFloat(row.subTotal) || 0);
    });
    return Object.entries(grouped).map(([month, value]) => ({
      month,
      value: Math.round(value * 100) / 100,
    }));
  }, [boxes, bookings]);

  return (
    <div>
      <Box top>
        <div className="ud-dash space-y-7 pb-6 px-1 sm:px-0">
          <section className="ud-hero relative overflow-hidden rounded-2xl border border-[#e0d0c4] px-5 py-6 sm:px-7 sm:py-7">
            <div className="ud-hero-glow" aria-hidden />
            <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-xl">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#c2410c]">
                  Sundarban Cargo · Agent
                </p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-stone-900 leading-tight">
                  {greeting},{" "}
                  <span className="text-[#6D4C41]">{displayName}</span>
                </h1>
                <p className="mt-2 text-sm sm:text-base text-stone-600 max-w-md leading-relaxed">
                  Book parcels, track box limits, and review commission from one
                  counter workspace.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="rounded-xl border border-[#e8ddd4] bg-white/80 backdrop-blur px-4 py-3 min-w-[120px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Bookings
                  </p>
                  <p className="text-2xl font-black tabular-nums text-stone-900">
                    {loading && !fetchedData ? "—" : stats.total}
                  </p>
                </div>
                <Link
                  to="/agency-bookings"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#6D4C41] to-[#423028] px-5 py-3.5 text-sm font-bold text-white shadow-md transition hover:brightness-110"
                >
                  New booking
                  <HiOutlineArrowRight className="text-base" />
                </Link>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-[#e8ddd4] bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
                Paid
              </p>
              <p className="mt-2 text-3xl font-black tabular-nums text-[#15803d]">
                {stats.paid}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {stats.unpaid} still open
              </p>
            </div>
            <div className="rounded-2xl border border-[#e8ddd4] bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
                Commission
              </p>
              <p className="mt-2 text-3xl font-black tabular-nums text-stone-900">
                {formatCurrency(stats.commission, stats.currency)}
              </p>
              <p className="mt-1 text-xs text-stone-500">Agent payable</p>
            </div>
            <div className="rounded-2xl border border-[#e8ddd4] bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
                Booking volume
              </p>
              <p className="mt-2 text-3xl font-black tabular-nums text-stone-900">
                {formatCurrency(stats.volume, stats.currency)}
              </p>
              <p className="mt-1 text-xs text-stone-500">Subtotal of parcels</p>
            </div>
            <div className="rounded-2xl border border-[#e8ddd4] bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
                Box limit
              </p>
              <p className="mt-2 text-3xl font-black tabular-nums text-[#ea580c]">
                {stats.used}
                <span className="text-lg text-stone-400 font-bold">
                  /{stats.limit || "—"}
                </span>
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {stats.remaining} remaining this month
              </p>
            </div>
          </section>

          {boxes.length > 0 ? (
            <section>
              <div className="mb-3">
                <h2 className="text-lg font-bold text-stone-900">Box allocation</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  Monthly quota for each package type
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {boxes.map((item, idx) => (
                  <BookingRemaining key={item.id || item.boxName || idx} data={item} />
                ))}
              </div>
            </section>
          ) : null}

          <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-stretch">
            <div className="ad-chart-card rounded-2xl border border-[#e8ddd4] bg-white p-4 sm:p-5 shadow-sm min-h-[320px]">
              <div className="mb-1">
                <h2 className="text-lg font-bold text-stone-900">
                  {boxes.length ? "Boxes assigned" : "Parcel cost"}
                </h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  {boxes.length
                    ? "Current month assignments by box type"
                    : "Value by parcel type"}
                </p>
              </div>
              {barDataset.length ? (
                <BarChartComponent
                  dataset={barDataset}
                  dataKey="value"
                  label={boxes.length ? "Assigned" : "Parcel Cost"}
                  axisLabel={boxes.length ? "Count" : "Cost (£)"}
                  valuePrefix={boxes.length ? "" : "£"}
                />
              ) : (
                <p className="text-sm text-stone-500 py-16 text-center">
                  No chart data yet
                </p>
              )}
            </div>
            <div className="ad-chart-card rounded-2xl border border-[#e8ddd4] bg-white p-4 sm:p-5 shadow-sm">
              <h2 className="text-lg font-bold text-stone-900">
                {stats.limit > 0 ? "Monthly box limit" : "Payment mix"}
              </h2>
              <p className="text-xs text-stone-500 mt-0.5 mb-1">
                {stats.limit > 0
                  ? "Used vs remaining allocation"
                  : "Paid against open bookings"}
              </p>
              {pieData.length ? (
                <PieChartComponenet
                  hideTitle
                  innerRadius={52}
                  data={pieData}
                  tittle={stats.limit > 0 ? "Monthly Booking Limit" : "Payment mix"}
                />
              ) : (
                <p className="text-sm text-stone-500 py-10 text-center">
                  No chart data yet
                </p>
              )}
            </div>
          </section>
        </div>
      </Box>

      <Box>
        <section className="ud-history rounded-2xl border border-[#e8ddd4] bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#efe6df] bg-[#fcf8f5] px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#6D4C41] text-white shadow-sm">
                <FaBoxOpen className="text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-stone-900 leading-tight">
                  Booking history
                </h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  Agent parcels, payment, and commission
                </p>
              </div>
            </div>
            {stats.total > 0 ? (
              <span className="inline-flex self-start sm:self-auto items-center gap-1.5 rounded-lg border border-[#e8ddd4] bg-white px-3 py-1.5 text-xs font-bold text-stone-600">
                <FaSearch className="text-[10px] text-stone-400" />
                {stats.total} booking{stats.total === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          <div className="p-4 sm:p-5">
            <PaymentHistoryTable
              notEditAble
              fetchedData={fetchedData}
              headers={[
                "parcel Creation Date",
                "sender Name",
                "sender Email",
                "receiver Name",
                "receiver Email",
                "route Name",
                "current status",
                "payment status",
                "cargo Type Name",
                "shipping Service Name",
                "weight Description",
                "dimension Name",
                "dimension Cost",
                "commision Percentage",
                "fixed Commision Amount",
                "agent Commision Payable",
                "sub Total",
              ]}
              row={[
                "parcelCreationDate",
                "senderName",
                "senderEmail",
                "receiverName",
                "receiverEmail",
                "routeName",
                "currentStatus",
                "paymentStatus",
                "cargoTypeName",
                "shippingServiceName",
                "weightDescription",
                "dimensionName",
                "dimensionCost",
                "commisionPercentage",
                "fixedCommisionAmount",
                "agentCommisionPayable",
                "subTotal",
              ]}
            />
          </div>
        </section>
      </Box>
    </div>
  );
};

export default Subroot;
